import { msg } from '@lingui/core/macro';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import { templatePresets, TemplatePresetsNameList } from 'modules/template-editor/domain/consts/form/template/template-presets';
import { useLingui } from '@lingui/react/macro';
import { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useIsAdmin } from 'shared/hooks/use-is-admin';
import { useToastApiError } from 'shared/hooks/use-toast-api-error';
import { UDText } from 'ud-ui/components';
import Modal from 'ud-ui/components/modal';
import { getAccessOptions, getVisibilityOptions } from 'modules/template-editor/domain/consts/form/template/options';
import { Values } from 'modules/template-editor/domain/interface/form/template';
import valueTypeValidator from 'modules/template-editor/domain/utils/form/template/value-type-validator';
import { useCreateBoardTemplateMutation } from 'modules/template-editor/store/board-templates-api';

import TemplateForm from 'modules/template-editor/ui/components/elements/forms/template-form';
import { useOwnerBoardTemplates } from 'modules/template-editor/ui/hooks/use-owner-board-templates';

const PRESET: TemplatePresetsNameList = 'create';

function useCreateModal() {
  const { t } = useLingui();
  const toastApiError = useToastApiError();
  const { info } = useOrganizer();
  const { templates } = useOwnerBoardTemplates();
  const [createTemplate, { isLoading }] = useCreateBoardTemplateMutation();
  const isAdmin = useIsAdmin();

  const location = useLocation();
  const isCatalog = location.pathname.includes('catalog');

  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => {
    setIsOpen(true);
  }, []);
  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onSubmit = useCallback(
    // `async` + промис запроса — Formik держит кнопку формы запертой до ответа (IMGM-1561). Окно закрывает
    // ответ, а шаблон к этому моменту уже в кэше списка (IMGM-1566).
    async (values: Values) => {
      if (!info) {
        throw Error('failed to create template: info is undefined');
      }
      const { width, height, name, visibility, access } = valueTypeValidator({
        values,
      });
      const resolution = { width: +width, height: +height };
      try {
        await createTemplate({
          name,
          ownerId: info.id,
          resolution,
          createStartBoard: true,
          sportType: info.sportType,
          visibility,
          access,
        }).unwrap();
        close();
      } catch (error) {
        toastApiError(error, msg`Не удалось создать шаблон`);
      }
    },
    [close, createTemplate, info, toastApiError],
  );

  const initialValue = useMemo(
    () => ({
      width: '1920',
      height: '1080',
      name: '',
      visibility: getVisibilityOptions().find((option) => option.value === 'all') ?? getVisibilityOptions()[0],
      access: getAccessOptions().find((option) => option.value === (isCatalog ? 'default' : 'private')) ?? getAccessOptions()[0],
    }),
    [isCatalog],
  );

  const existingNames = useMemo(() => templates.map((template) => template.name.trim()), [templates]);

  return {
    modal: (
      <Modal show={isOpen} onClose={close} size="md">
        <UDText type={'title-3'} className="mb-5">
          {t(templatePresets[PRESET].header)}
        </UDText>
        <TemplateForm
          preset={PRESET}
          initialValues={initialValue}
          onSubmit={onSubmit}
          existingNames={existingNames}
          advancedAccess={isAdmin && isCatalog}
          isLoading={isLoading}
        />
      </Modal>
    ),
    close,
    open,
  };
}

export default useCreateModal;
